/**
 * The camp on the field east of the apron: thirty-one tents somebody slept in,
 * and the kiertokalusto - the fence, the bins and the portaloos - that lines
 * the way past them.
 *
 * The tents' footprints and ridge heights are hashed through `campTentBox`;
 * their colours are not.
 */

import { solid, type CampTentSpec, type SolidSpec } from '../world/Level';
import { clamp } from '../utils/math';
import { mulberry32, randRange } from '../utils/prng';
import { TORNI } from './levelPorukka';
import { floors } from './levelFloors';

/* ------------------------------------------------------------ telttaleiri - */

/**
 * ==========================================================================
 *  TELTTALEIRI: the field east of the juhlanavetta
 * ==========================================================================
 *  Thirty-odd people came for two nights and almost all of them sleep under
 *  a roof - the tornimajoitus, the sali floor, a sofa each. Seven brought a
 *  tent, and all seven are up on the grass east of the apron: the one corner
 *  of the pihapiiri big enough to put a camp on, and the one corner with
 *  nothing on it at all: come round the palju and the whole right half of the
 *  view was mown horizon between the barn and the treeline.
 *
 *  SEVEN TENTS IN A PLOT, not seven tents in a field. Whoever got there first
 *  took the grass nearest the door and nobody after him walked any further,
 *  which is both what happens and the only way this reads at all: the same
 *  seven spread over the whole field is litter dropped on a lawn, and a camp
 *  is something you recognise by how tightly it is packed. So the plot is
 *  three rows deep, it stops level with the tornimajoitus, and the grass
 *  south and east of it is left empty.
 *
 *  WHERE IT STARTS is not typed. The camp begins where the gravel ends: the
 *  navetan piha is what the cars turn round on, nobody pitches a tent on a
 *  turning circle, so the west edge is read off that floor and follows it if
 *  it ever moves. Nor is where it ends in the east, which is the far side of
 *  the tornimajoitus and read off the tower. The other two edges are the path
 *  worn up to it and how far south seven tents reach.
 *
 *  All tents are solid. The seven staggered pitches stay east of the apron;
 *  `KIERTOKALUSTO` blocks the straight shortcut beyond the trees.
 *  Their effective bounds belong to the simulation fingerprint.
 *
 *  WHY THEY ARE HARJATELTAT and not anything rounder: this field is looked at
 *  from twenty to forty metres away, across grass, and the one thing it has
 *  to do is read as *tents*. A ridge is a silhouette at that distance; a dome
 *  is a coloured lump. Same argument as the kuusi, one shape further on.
 *
 *  NOBODY PACES OUT A CAMPSITE, so the grid below is a starting point and not
 *  a plan: each pitch is rolled off it, and a pitch that lands too close to
 *  one already up is rolled again and then given up on. Giving up is what
 *  leaves the field its holes - a camp with a tent in every square is a car
 *  park with the lines rubbed out.
 * ==========================================================================
 */
export const LEIRI = {
  /**
   * Grass between the gravel and the nearest guy line. The edge itself is read
   * off `navetan-piha` rather than typed, so the camp cannot end up pitched on
   * the turning circle if the apron is ever redrawn.
   */
  offGravel: 1.2,
  /**
   * The west edge has two pieces, because the ground does. Alongside the apron
   * it is the gravel's own edge and the number above; south of the apron the
   * gravel stops and the field reaches on west across the lawn, and this is as
   * far as it goes.
   *
   * What stops it there is the one line on this side of the yard that is
   * actually run: coming round the north end of `KUUSIKKO_KAARRE` and down
   * past the pitkä pöytä is the long way to the door (README, *Piha-alue*),
   * and it comes down at about x = 137. Six metres east of that is a camp
   * beside the treeline rather than a camp in the way of it, and it is the
   * clearance `Level.test.ts` measures.
   */
  lawnX: 143.5,
  /**
   * East edge, and the one edge that ends at a building rather than at a
   * number: nobody carried a tent past the tornimajoitus. Read off the tower
   * so the two cannot drift apart - seen from the yard the pihapiiri stops at
   * the silo, and a tent east of it is a tent on its own out in the field.
   */
  eastX: TORNI.x + TORNI.radius,
  /**
   * North edge. South of `polku-tornille`, which leaves the apron's corner at
   * z = -24.2 - a path up to the door people walk in the dark, and not
   * somewhere to string a guy line across.
   */
  northZ: -23.2,
  /**
   * And the south one, which is simply how far down the field seven tents
   * reach: three rows off the grid below, and mown grass after that.
   */
  southZ: -6.5,
  /** Pitch to pitch before anybody moved: one tent and a lane to walk down. */
  spacing: { x: 5.2, z: 4.9 },
  /** How far off that mark a pitch may land. */
  jitter: 1.2,
  /**
   * Daylight between two flysheets, at the closest corners either of them
   * has. The whole point of the camp is that it is a camp rather than a row,
   * so this is enforced when the pitch is rolled: see `telttaleiri`.
   */
  pitchClearance: 1.4,
  /** Tighten the same seven pitches without rerolling their sizes or colours. */
  packing: { center: [155, -16], scale: 0.85 },
  /** Minimum corner-circle clearance after packing. */
  clearance: 0.9,
  /** Rolls before a pitch is given up on and left empty. */
  tries: 8,
  /** And the fraction of pitches nobody took in the first place. */
  vacancy: 0.1,
  /**
   * A two-man tent and a family one, in metres: floor along the ridge, floor
   * across it, and the ridge itself. Real sizes - a two-man tent is 2.4 m of
   * groundsheet, and drawing one at four is what turns a campsite into a row
   * of marquees.
   */
  small: { length: [2.3, 2.9], width: [1.45, 1.85], height: [1.05, 1.3] },
  large: { length: [3.3, 4.0], width: [2.2, 2.7], height: [1.45, 1.75] },
  /** How many of them are the big one. */
  largeShare: 0.28,
  /**
   * Which way a door faces: out at the yard, give or take. Everybody pitched
   * looking at the barn because that is where the party was, and nobody got it
   * square. Read the same way as `SignSpec.rotationY`, so -PI/2 is due west.
   */
  facing: -Math.PI / 2,
  facingSpread: 1.05,
  /**
   * The flysheets. Every colour a Finnish garage has a tent in, and no two
   * neighbours the same - the index steps by a number coprime with the length
   * of this list, which is what guarantees that. The list is longer than the
   * camp is, so as it stands no two of the seven share a colour at all.
   */
  colors: [
    0x2f6fb8, // sininen
    0xc23b2c, // punainen
    0x2f7d45, // metsänvihreä
    0xe0a72c, // keltainen
    0x50a8a0, // turkoosi
    0xd2703a, // oranssi
    0x8d6bb0, // violetti
    0xd9d5c8, // hiekka
    0x9fbc3c, // limenvihreä
    0x3b4a63, // yönsininen
  ],
  colorStep: 3,
  /**
   * The door, as a fraction of the gable it is cut in. A similar triangle on
   * the same base line, so its edges run parallel to the ones either side of
   * it however the tent is proportioned - which is what a zipped-up door
   * actually looks like, and what stops it poking out through the seam.
   */
  door: 0.62,
  /** How much darker that doorway is than the fly around it. */
  doorShade: 0.34,
  /**
   * The groundsheet showing under the hem. Without it a coloured shape sits
   * *on* the grass rather than *in* it - a tent is pegged down, and the dark
   * line round the bottom of one is the whole of what says so from thirty
   * metres.
   */
  hem: { reach: 0.08, height: 0.07, shade: 0.5 },
} as const;

/**
 * The camp, rolled once at module load off a fixed seed - authoring data, like
 * the klapiteline's stack, and under the same rule: `src/utils/prng.ts` is
 * seeded and runs once, so this is the same field on every machine and in
 * every replay.
 *
 * Two things are guaranteed rather than hoped for, because both are what the
 * field is *for*. A tent stands wholly inside the four edges above - the
 * pitch is clamped by the tent's own size, so nobody is half on the gravel or
 * across the path. Before packing, no two come within `LEIRI.pitchClearance` of each
 * other, measured circle to circle off each footprint's diagonal, which is
 * conservative whichever way the two are turned and cheap enough to ask for
 * every pair. A pitch that cannot be made to fit is left empty.
 */
function telttaleiri(): CampTentSpec[] {
  const gravel = floors.find((item) => item.name === 'navetan-piha');
  if (!gravel) throw new Error('telttaleiri: no navetan-piha to pitch east of');
  const gravelEastX = gravel.center[0] + gravel.size[0] / 2;
  const gravelSouthZ = gravel.center[1] + gravel.size[1] / 2;
  // Beside the apron, the gravel. Past its south edge, the lawn - see
  // `LEIRI.lawnX`, and note that the step is read off the same floor the edge
  // is, so the two halves cannot drift apart.
  const westAt = (z: number): number =>
    z > gravelSouthZ + LEIRI.offGravel ? LEIRI.lawnX : gravelEastX + LEIRI.offGravel;

  const rng = mulberry32(0x7e17a5);
  const pitched: { x: number; z: number; radius: number }[] = [];
  const tents: CampTentSpec[] = [];
  let colour = 0;

  // Half a pitch in from each edge: the marks are where the rows would be if
  // anybody had set them out, and every tent is rolled off one of them.
  for (let cz = LEIRI.northZ + LEIRI.spacing.z / 2; cz <= LEIRI.southZ; cz += LEIRI.spacing.z) {
    const westX = westAt(cz);
    for (let cx = westX + LEIRI.spacing.x / 2; cx <= LEIRI.eastX; cx += LEIRI.spacing.x) {
      if (rng() < LEIRI.vacancy) continue;

      const size = rng() < LEIRI.largeShare ? LEIRI.large : LEIRI.small;
      const length = randRange(rng, size.length[0], size.length[1]);
      const width = randRange(rng, size.width[0], size.width[1]);
      const height = randRange(rng, size.height[0], size.height[1]);
      // The circle the footprint fits inside, whichever way it is turned. Both
      // the edges and the neighbours are measured against this rather than
      // against the rectangle, so neither answer depends on the yaw.
      const radius = Math.hypot(length, width) / 2;

      for (let attempt = 0; attempt < LEIRI.tries; attempt++) {
        const x = clamp(
          cx + randRange(rng, -LEIRI.jitter, LEIRI.jitter),
          westX + radius,
          LEIRI.eastX - radius,
        );
        const z = clamp(
          cz + randRange(rng, -LEIRI.jitter, LEIRI.jitter),
          LEIRI.northZ + radius,
          LEIRI.southZ - radius,
        );
        const clear = pitched.every(
          (other) => Math.hypot(x - other.x, z - other.z) >= radius + other.radius + LEIRI.pitchClearance,
        );
        if (!clear) continue;

        pitched.push({ x, z, radius });
        tents.push({
          name: `leiriteltta-${tents.length + 1}`,
          center: [x, z],
          length,
          width,
          height,
          rotationY: LEIRI.facing + randRange(rng, -LEIRI.facingSpread, LEIRI.facingSpread),
          color: LEIRI.colors[colour % LEIRI.colors.length],
        });
        colour += LEIRI.colorStep;
        break;
      }
    }
  }

  return tents.map((tent) => ({
    ...tent,
    center: [
      LEIRI.packing.center[0] + (tent.center[0] - LEIRI.packing.center[0]) * LEIRI.packing.scale,
      LEIRI.packing.center[1] + (tent.center[1] - LEIRI.packing.center[1]) * LEIRI.packing.scale,
    ],
  }));
}

export const TELTTALEIRI = telttaleiri();

/** Rear stays at the trees; 90% vehicle size gives a roughly two-second shorter right turn. */
export const KIERTOKALUSTO: SolidSpec[] = [
  { ...solid('kiertoperakarry', 'carAlt', 157.05, 1.44, 168.975, 4.5, 2.97), shape: 'trailer' },
  { ...solid('kiertovetoaisa', 'metal', 168.975, 2.75, 170.175, 3.19, 1.3), shape: 'drawbar' },
  { ...solid('kiertotraktori', 'car', 170.175, 1.44, 176.295, 4.5, 3.15), shape: 'tractor' },
];
